import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Moon, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import LanguageSelector from "@/components/LanguageSelector";

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-mist via-white to-ethereal/20">
      {/* Navigation */}
      <nav className="glass-effect border-b border-white/60 sticky top-0 z-50 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/logo-new.png" alt="Uneksia" className="w-12 h-12" />
              <span className="text-2xl font-heading font-light text-void tracking-tight">{t('appName')}</span>
            </div>
            <LanguageSelector variant="minimal" />
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-void hover:text-void/80 font-body"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back') || 'Back'}
        </Button>

        <div className="glass-effect rounded-3xl p-8 md:p-12">
          <h1 className="text-4xl font-heading font-light text-void mb-2">
            Tietosuojakäytäntö
          </h1>
          <p className="text-void/60 font-body mb-8">Privacy Policy</p>
          
          <p className="text-sm text-void/50 font-body mb-8">
            Päivitetty / Last updated: 23.2.2026
          </p>

          <div className="space-y-8 text-void/80 font-body">
            {/* Finnish */}
            <section>
              <h2 className="text-xl font-heading text-void mb-4">Tietojen kerääminen</h2>
              <p className="mb-3">
                Unipäiväkirja kerää seuraavia tietoja:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Sähköpostiosoite ja nimi (rekisteröityminen)</li>
                <li>Unipäiväkirjamerkinnät (tallentamasi unet)</li>
                <li>Käyttötilastot (Google Analytics)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-heading text-void mb-4">Tietojen käyttö</h2>
              <p>
                Tietojasi käytetään vain palvelun tarjoamiseen. Emme myy tai jaa tietojasi kolmansille osapuolille markkinointitarkoituksiin.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-heading text-void mb-4">Tietojen suojaus</h2>
              <p>
                Unesi ja henkilötietosi ovat oletuksena yksityisiä. Sinä päätät, mitä jaat julkisesti. Käytämme SSL-salausta ja turvallisia palvelimia tietojesi suojaamiseen.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-heading text-void mb-4">Tietojen poistaminen</h2>
              <p>
                Voit milloin tahansa poistaa tilisi ja kaikki siihen liittyvät tiedot ottamalla yhteyttä osoitteeseen uneksia@unipäiväkirja.com.
              </p>
            </section>

            <hr className="border-void/10 my-8" />

            {/* English */}
            <section>
              <h2 className="text-xl font-heading text-void mb-4">Data Collection</h2>
              <p className="mb-3">
                Unipäiväkirja collects the following information:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Email address and name (registration)</li>
                <li>Dream journal entries (dreams you save)</li>
                <li>Usage statistics (Google Analytics)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-heading text-void mb-4">Data Usage</h2>
              <p>
                Your data is used only to provide the service. We do not sell or share your information with third parties for marketing purposes.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-heading text-void mb-4">Data Protection</h2>
              <p>
                Your dreams and personal data are private by default. You control what you share publicly. We use SSL encryption and secure servers to protect your data.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-heading text-void mb-4">Data Deletion</h2>
              <p>
                You can delete your account and all associated data at any time by contacting uneksia@unipäiväkirja.com.
              </p>
            </section>

            <hr className="border-void/10 my-8" />

            <section>
              <h2 className="text-xl font-heading text-void mb-4">Yhteystiedot / Contact</h2>
              <p>
                uneksia@unipäiväkirja.com
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
